import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Check, Zap, Crown, CreditCard, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESSENTIALS_FEATURES = [
  "75 créditos mensuales",
  "Desarrollo de aplicaciones móviles",
  "Alojamiento de proyectos privados",
  "Integración con Github",
  "Fork de tareas",
  "Soporte por email"
];

const PLUS_FEATURES = [
  "375 créditos mensuales",
  "Todo lo incluido en Essentials",
  "Agente CloudT1 avanzado",
  "Ventana de contexto 1M tokens",
  "Pensamiento ultra",
  "Editar prompt del sistema",
  "Crear agentes personalizados",
  "Soporte prioritario"
];

const CREDIT_PACKAGES = [
  { id: "starter", credits: 25, price: 5 },
  { id: "small", credits: 50, price: 10 },
  { id: "medium", credits: 150, price: 25 },
  { id: "large", credits: 400, price: 50 },
  { id: "enterprise", credits: 1000, price: 100 }
];

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleSubscribe = async (planBase) => {
    if (!user) {
      navigate('/register');
      return;
    }

    const planId = billingCycle === 'annual' ? `${planBase}_annual` : `${planBase}_monthly`;
    setLoading(planId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error al procesar el pago');
    } finally {
      setLoading(null);
    }
  };

  // Check if user has an active subscription
  const hasActiveSubscription = user && user.plan && 
    ['essentials_monthly', 'essentials_annual', 'plus_monthly', 'plus_annual'].includes(user.plan);

  const handleBuyCredits = async (packageId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (!hasActiveSubscription) {
      toast.error('Debes tener una suscripción activa para comprar créditos adicionales');
      return;
    }

    setLoading(packageId);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        credits_package: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setLoading(null);
    }
  };

  const getPrice = (monthlyPrice) => {
    if (billingCycle === 'annual') {
      return Math.round(monthlyPrice * 12 * 0.8);
    }
    return monthlyPrice;
  };

  const getCredits = (monthlyCredits) => {
    if (billingCycle === 'annual') {
      return monthlyCredits * 12;
    }
    return monthlyCredits;
  };

  return (
    <div className="app-container min-h-screen">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">{t('pricing.title')}</h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">{t('pricing.subtitle')}</p>
            
            <div className="inline-flex items-center gap-2 p-1 rounded-full bg-white/5 border border-white/10">
              <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-[#FF6B00] text-white' : 'text-gray-400 hover:text-white'}`}>
                Mensual
              </button>
              <button onClick={() => setBillingCycle('annual')} className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-[#FF6B00] text-white' : 'text-gray-400 hover:text-white'}`}>
                Anual
                <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">-20%</span>
              </button>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-8 border border-white/10" data-testid="pricing-card-essentials">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-blue-500/20"><Zap className="w-5 h-5 text-blue-400" /></div>
                <h3 className="text-xl font-bold text-white">Essentials</h3>
              </div>
              <p className="text-gray-400 text-sm mb-6">Perfecto para constructores principiantes</p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-white">${getPrice(15)}</span>
                <span className="text-gray-500">/ {billingCycle === 'annual' ? 'año' : 'mes'}</span>
              </div>
              {billingCycle === 'annual' && <p className="text-sm text-green-400 mb-2">$12/mes (Ahorras $36)</p>}
              <p className="text-sm text-gray-400 mb-6">{getCredits(75)} créditos {billingCycle === 'annual' ? 'al año' : 'al mes'}</p>
              
              {(user?.plan === 'essentials_monthly' || user?.plan === 'essentials_annual') ? (
                <div className="w-full py-3 rounded-lg bg-white/5 border border-white/10 text-center text-gray-400 text-sm font-medium mb-6">Plan Actual</div>
              ) : (
                <button onClick={() => handleSubscribe('essentials')} disabled={loading !== null} className="w-full py-3 rounded-lg font-semibold text-sm border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white transition-colors mb-6 flex items-center justify-center gap-2" data-testid="subscribe-essentials-btn">
                  {loading?.includes('essentials') ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Suscribirse'}
                </button>
              )}
              
              <div className="space-y-3">
                {ESSENTIALS_FEATURES.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" /><span className="text-sm text-gray-300">{feature}</span></div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass rounded-2xl p-8 border-2 border-[#FF6B00]/50 relative" data-testid="pricing-card-plus">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FF6B00] text-white text-xs font-semibold">Más Popular</div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-orange-500/20"><Crown className="w-5 h-5 text-orange-400" /></div>
                <h3 className="text-xl font-bold text-white">Plus</h3>
              </div>
              <p className="text-gray-400 text-sm mb-6">Para creadores y marcas serias</p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-white">${getPrice(100)}</span>
                <span className="text-gray-500">/ {billingCycle === 'annual' ? 'año' : 'mes'}</span>
              </div>
              {billingCycle === 'annual' && <p className="text-sm text-green-400 mb-2">$80/mes (Ahorras $240)</p>}
              <p className="text-sm text-gray-400 mb-6">{getCredits(375)} créditos {billingCycle === 'annual' ? 'al año' : 'al mes'}</p>
              
              {(user?.plan === 'plus_monthly' || user?.plan === 'plus_annual') ? (
                <div className="w-full py-3 rounded-lg bg-white/5 border border-white/10 text-center text-gray-400 text-sm font-medium mb-6">Plan Actual</div>
              ) : (
                <button onClick={() => handleSubscribe('plus')} disabled={loading !== null} className="w-full py-3 rounded-lg font-semibold text-sm bg-[#FF6B00] text-white hover:bg-[#FF8533] transition-colors mb-6 flex items-center justify-center gap-2" data-testid="subscribe-plus-btn">
                  {loading?.includes('plus') ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Suscribirse'}
                </button>
              )}
              
              <div className="space-y-3">
                {PLUS_FEATURES.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-400" /><span className="text-sm text-gray-300">{feature}</span></div>
                ))}
              </div>
            </motion.div>
          </div>

          {user && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Comprar Créditos Adicionales</h2>
                <p className="text-gray-400">Pago único para suscriptores activos</p>
              </div>
              
              {!hasActiveSubscription && (
                <div className="max-w-md mx-auto mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="subscription-required-notice">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <CreditCard className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-amber-300 font-medium text-sm">Suscripción requerida</p>
                      <p className="text-amber-400/70 text-xs">Suscríbete a un plan para comprar créditos adicionales</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
                {CREDIT_PACKAGES.map((pkg) => (
                  <div key={pkg.id} className={`glass rounded-xl p-4 text-center border transition-colors ${hasActiveSubscription ? 'border-white/10 hover:border-white/20' : 'border-white/5 opacity-60'}`} data-testid={`credit-package-${pkg.id}`}>
                    <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-gradient-to-br from-[#FF6B00]/20 to-orange-500/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-[#FF6B00]" />
                    </div>
                    <p className="text-xl font-bold text-white mb-1">{pkg.credits}</p>
                    <p className="text-xs text-gray-500 mb-3">créditos</p>
                    <p className="text-lg font-semibold text-white mb-3">${pkg.price}</p>
                    <button 
                      onClick={() => handleBuyCredits(pkg.id)} 
                      disabled={loading === pkg.id || !hasActiveSubscription} 
                      className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                        hasActiveSubscription 
                          ? 'border border-[#FF6B00]/50 text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white' 
                          : 'border border-gray-600 text-gray-500 cursor-not-allowed'
                      }`}
                      data-testid={`buy-credits-${pkg.id}-btn`}
                    >
                      {loading === pkg.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (hasActiveSubscription ? 'Comprar' : 'Requiere suscripción')}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
